import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import {
  DocumentIcon,
  DownloadIcon,
  ExportIcon,
  TrashIcon,
} from "../../../components/icons";

import { CrewDocumentDialog } from "../components/CrewDocumentDialog";
import { DocumentViewerDialog } from "../components/DocumentViewerDialog";
import { PilotProps } from "../types/view-pilot";
import {
  CrewDocumentInput,
  CrewDocumentRecord,
  getDocumentExtension,
  getDocumentName,
} from "../types/crew-documents";
import {
  countRequiredUploaded,
  downloadDocument,
  groupDocumentsByCategory,
} from "../utils/crewDocuments";
import { UseCrewDocuments } from "../hooks/useCrewDocuments";

interface CrewDocumentsProps extends Omit<UseCrewDocuments, "refresh"> {
  pilot: PilotProps;
}

export const CrewDocuments: React.FC<CrewDocumentsProps> = ({
  pilot,
  documents,
  loading,
  saving,
  uploadDocument,
  updateDocument,
  deleteDocument,
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<CrewDocumentRecord | null>(
    null,
  );
  const [presetTypeName, setPresetTypeName] = useState<string>("");
  const [recordPendingDelete, setRecordPendingDelete] =
    useState<CrewDocumentRecord | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const sections = useMemo(
    () => groupDocumentsByCategory(documents),
    [documents],
  );
  // Flattened in the order the sections are rendered, so the viewer's
  // previous/next matches what is on screen.
  const orderedDocuments = useMemo(
    () => sections.flatMap((section) => section.documents),
    [sections],
  );
  const requiredProgress = useMemo(
    () => countRequiredUploaded(documents),
    [documents],
  );
  const allRequiredUploaded =
    requiredProgress.uploaded === requiredProgress.total;

  const handleUploadClick = (typeName = "") => {
    setEditingRecord(null);
    setPresetTypeName(typeName);
    setIsDialogOpen(true);
  };

  const handleViewClick = (record: CrewDocumentRecord) => {
    const index = orderedDocuments.findIndex((item) => item.id === record.id);
    setViewerIndex(index >= 0 ? index : null);
  };

  const handleEditClick = (record: CrewDocumentRecord) => {
    setEditingRecord(record);
    setPresetTypeName("");
    setIsDialogOpen(true);
  };

  const handleDialogSubmit = async (input: CrewDocumentInput) => {
    if (editingRecord) return updateDocument(editingRecord, input);
    return uploadDocument(input);
  };

  const handleDeleteConfirm = async () => {
    if (!recordPendingDelete) return;
    const succeeded = await deleteDocument(recordPendingDelete);
    if (succeeded) setRecordPendingDelete(null);
  };

  if (!pilot) return null;

  return (
    <Box sx={{ p: 1, minHeight: "auto" }}>
      <Card sx={{ borderRadius: 2, boxShadow: 0, mb: 2 }}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Documents &amp; Certifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Files uploaded by {pilot.name || "this crew member"} or added
                here by an admin.
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={`${requiredProgress.uploaded} of ${requiredProgress.total} required uploaded`}
                color={allRequiredUploaded ? "success" : "warning"}
                size="small"
                sx={{ borderRadius: 1.5 }}
              />
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => handleUploadClick()}
                sx={{
                  backgroundColor: reduxTheme.primary_colors[400],
                  borderRadius: 1.5,
                  textTransform: "none",
                }}
              >
                Upload Document
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {loading && documents.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {sections.map((section) => (
            <Grid size={{ xs: 12, md: 6 }} key={section.type_name}>
              <Card sx={{ borderRadius: 2, boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={1}
                    sx={{
                      pb: "8px",
                      borderBottom: 1,
                      borderColor: "divider",
                      mb: "12px",
                    }}
                  >
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DocumentIcon color="disabled" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {section.type_name}
                        </Typography>
                        <Chip
                          label={section.required ? "Required" : "Optional"}
                          size="small"
                          color={
                            section.required
                              ? section.documents.length > 0
                                ? "success"
                                : "warning"
                              : "default"
                          }
                          variant={section.required ? "filled" : "outlined"}
                          sx={{ borderRadius: 1 }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {section.description}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleUploadClick(section.type_name)}
                      sx={{ textTransform: "none", borderRadius: 1.5, flexShrink: 0 }}
                    >
                      Upload
                    </Button>
                  </Box>

                  {section.documents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" py={1}>
                      Nothing uploaded yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {section.documents.map((record) => (
                        <Box
                          key={record.id}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={1}
                          sx={{
                            padding: 1,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: backgroundColor,
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{ minWidth: 0 }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                padding: 1,
                                bgcolor: backgroundColor,
                                flexShrink: 0,
                              }}
                            >
                              <DocumentIcon
                                color="disabled"
                                sx={{ fontSize: 20 }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                title={getDocumentName(record.document)}
                              >
                                {getDocumentName(record.document)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {getDocumentExtension(record.document)}
                              </Typography>
                            </Box>
                          </Box>

                          <Box display="flex" alignItems="center" flexShrink={0}>
                            <Tooltip title="View">
                              <IconButton
                                onClick={() => handleViewClick(record)}
                              >
                                <ExportIcon
                                  color="disabled"
                                  sx={{ fontSize: 18 }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                onClick={() => downloadDocument(record.document)}
                              >
                                <DownloadIcon
                                  color="info"
                                  sx={{ fontSize: 20 }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Replace or rename">
                              <IconButton onClick={() => handleEditClick(record)}>
                                <Edit color="disabled" sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={() => setRecordPendingDelete(record)}
                              >
                                <TrashIcon color="error" sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <DocumentViewerDialog
        open={viewerIndex !== null}
        onClose={() => setViewerIndex(null)}
        documents={orderedDocuments}
        activeIndex={viewerIndex ?? 0}
        onNavigate={setViewerIndex}
      />

      <CrewDocumentDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleDialogSubmit}
        saving={saving}
        record={editingRecord}
        defaultTypeName={presetTypeName}
      />

      <Dialog
        open={Boolean(recordPendingDelete)}
        onClose={saving ? undefined : () => setRecordPendingDelete(null)}
        aria-labelledby="delete-document-dialog-title"
      >
        <DialogTitle id="delete-document-dialog-title">
          Confirm Document Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <Typography component="span" fontWeight="bold">
              {recordPendingDelete
                ? getDocumentName(recordPendingDelete.document)
                : ""}
            </Typography>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRecordPendingDelete(null)}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={saving}
            startIcon={
              saving ? <CircularProgress color="inherit" size={16} /> : undefined
            }
            sx={{
              bgcolor: "error.main",
              "&:hover": { bgcolor: "error.dark" },
              color: "#fff",
              textTransform: "none",
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrewDocuments;
